import React, { useRef, useEffect } from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { Layout } from '@components';
import { Icon } from '@components/icons';

const StyledTableContainer = styled.div`
  margin: 100px -20px;

  @media (max-width: 768px) {
    margin: 50px -10px;
  }

  table {
    width: 100%;
    border-collapse: collapse;

    .hide-on-mobile {
      @media (max-width: 768px) {
        display: none;
      }
    }

    tbody tr {
      &:hover,
      &:focus {
        background-color: var(--light-navy);
      }
    }

    th,
    td {
      padding: 10px;
      text-align: left;

      &:first-child {
        padding-left: 20px;

        @media (max-width: 768px) {
          padding-left: 10px;
        }
      }
      &:last-child {
        padding-right: 20px;

        @media (max-width: 768px) {
          padding-right: 10px;
        }
      }

      svg {
        width: 20px;
        height: 20px;
      }
    }

    tr {
      cursor: default;

      td:first-child {
        border-top-left-radius: var(--border-radius);
        border-bottom-left-radius: var(--border-radius);
      }
      td:last-child {
        border-top-right-radius: var(--border-radius);
        border-bottom-right-radius: var(--border-radius);
      }
    }

    td {
      &.year {
        padding-right: 20px;

        @media (max-width: 768px) {
          padding-right: 10px;
          font-size: var(--fz-sm);
        }
      }

      &.title {
        padding-top: 15px;
        padding-right: 20px;
        color: var(--lightest-slate);
        font-size: var(--fz-xl);
        font-weight: 600;
        line-height: 1.25;
      }

      &.company {
        font-size: var(--fz-lg);
        white-space: nowrap;
      }

      &.tech {
        font-size: var(--fz-xxs);
        font-family: var(--font-mono);
        line-height: 1.5;
        .separator {
          margin: 0 5px;
        }
        span {
          display: inline-block;
        }
      }

      &.links {
        min-width: 100px;

        div {
          display: flex;
          align-items: center;

          a {
            ${({ theme }) => theme.mixins.flexCenter};
            flex-shrink: 0;
          }

          a + a {
            margin-left: 10px;
          }
        }
      }
    }
  }
  .lien
  {
    font-size:130%;
    color:#64ffda;
    text-decoration: underline;

  }
  

`;

const ArchivePage = ({ location, data }) => {
  const esports = data.allMarkdownRemark.edges;
  const revealTitle = useRef(null);
  const revealTable = useRef(null);
  const revealProjects = useRef([]);

  useEffect(() => {
    sr.reveal(revealTitle.current, srConfig());
    sr.reveal(revealTable.current, srConfig(200, 0));
    revealProjects.current.forEach((ref, i) => sr.reveal(ref, srConfig(i * 10)));
  }, []);

  return (
    <Layout location={location}>
      <Helmet title="Esport" />

      <main>
        <header ref={revealTitle}>
          <h1 className="big-heading">Esport : a passion</h1>
          <p className="subtitle">A quick recap of my, or rather, his prize list: <a href="https://lolpros.gg/player/nemesis-1">4es Némésis</a> <br/></p>
          <p>
            During my time at my engineering school, I was able to take part in a number of tournaments as part of the 4eSport association.
          <br/>
          Notably at the League of Legends University Championship : <a href="https://fr.euw.leagueoflegends.com/university/format">La Grosse Ligue</a><br/>
          Bi-annual Championship, which we won, qualifying us for the <a href="https://www.efrei.fr/4esport-aux-championnats-europeens-de-l-o-l/">UEM</a> (University Esports Masters) in Madrid.
          <br/>These competitive experiences have forged my desire to perform and my team spirit. 
          </p>
        </header>

        <StyledTableContainer ref={revealTable}>
          <table>
            <thead>
              <tr>
                <th>Year</th>
                <th>Tournament</th>
                <th className="hide-on-mobile">Results</th>
                <th className="hide-on-mobile">Feedback</th>
                <th>More</th>
              </tr>
            </thead>
            <tbody>
            
            <tr>  
              <td className="overline year">2021</td>
              <td className="title">UNIVERSITY ESPORTS MASTERS</td>
              <td className="company hide-on-mobile">5<sup>th</sup></td>
              <td className="tech hide-on-mobile">8 matches exclusively online, against teams representing Poland, Turkey, Italy and Germany</td>
              <td className="links">
                <a href="https://youtu.be/RPl7VxlDstc?list=PLEa48h7Kn5o3wYeneMwYEePAgC8PGlwff" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>

            <tr>  
              <td className="overline year">2021</td>
              <td className="title">Grosse Ligue 5<sup>th</sup> segment</td>
              <td className="company hide-on-mobile">2<sup>nd</sup></td>
              <td className="tech hide-on-mobile">Ticket for UEM 2021</td>
              <td className="links">
                <a href="https://youtu.be/RPl7VxlDstc?list=PLEa48h7Kn5o3wYeneMwYEePAgC8PGlwff" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>


             <tr>  
              <td className="overline year">2020</td>
              <td className="title">Grosse Ligue 4<sup>th</sup> segment</td>
              <td className="company hide-on-mobile">4<sup>th</sup></td>
              <td className="tech hide-on-mobile">Return to competition for 4eSport after a semester abroad for the whole team</td>
              <td className="links">
                
              </td>
             </tr>
             
             <tr>  
              <td className="overline year">2020</td>
              <td className="title">Nantarena</td>
              <td className="company hide-on-mobile">1<sup>st</sup></td>
              <td className="tech hide-on-mobile">Lan in Nante, team victory</td>
              <td className="links">
                <a href="https://www.facebook.com/nantarena/photos/a.3258676930828186/3258704417492104" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>

             <tr>  
              <td className="overline year">2019</td>
              <td className="title">PlayTest World Championship</td>
              <td className="company hide-on-mobile">----</td>
              <td className="tech hide-on-mobile">Preparing as an extra for the world championship final at the Bercy arena</td>
              <td className="links">
                <a href="https://youtu.be/D07FxBwxZ64" target="_blank" aria-label="External Link">
                  <Icon name="Folder" />
                </a>
                
              </td>
             </tr>

             <tr>  
              <td className="overline year">2019</td>
              <td className="title">UNIVERSITY ESPORTS MASTERS</td>
              <td className="company hide-on-mobile">5<sup>th</sup></td>
              <td className="tech hide-on-mobile">Wonderful experience in Madrid, 4 days really enriching days.</td>
              <td className="links">
                <a href="https://youtu.be/tsP6mlctS9U?list=PLEa48h7Kn5o3wYeneMwYEePAgC8PGlwff" target="_blank" aria-label="External Link">
                  <Icon name="Youtube" />
                </a>
                <a href="https://www.efrei.fr/4esport-aux-championnats-europeens-de-l-o-l/" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>

             <tr>  
              <td className="overline year">2019</td>
              <td className="title">Grosse Ligue 2<sup>nd</sup> segment</td>
              <td className="company hide-on-mobile">1<sup>st</sup></td>
              <td className="tech hide-on-mobile"><a href="https://www.efrei.fr/wp-content/uploads/2019/06/20190618-CP-EfreiParis-4eSport.pdf" class="lien">French Student Champion</a> -> Ticket for European competition</td>
              <td className="links">
                <a href="https://www.efrei.fr/4esport-champions-de-france-league-of-legends/" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>

             <tr>  
              <td className="overline year">2019</td>
              <td className="title">Occitanie Esport</td>
              <td className="company hide-on-mobile">2<sup>nd</sup></td>
              <td className="tech hide-on-mobile">First solo lan without 4eSport, amateur tree</td>
              <td className="links">
               
              </td>
             </tr>

             <tr>  
              <td className="overline year">2019</td>
              <td className="title">DreamHack</td>
              <td className="company hide-on-mobile">5<sup>th</sup></td>
              <td className="tech hide-on-mobile">Lan in Toulouse, first match on stage</td>
              <td className="links">
                <a href="https://youtu.be/gTrswfK3ji0?list=PLEa48h7Kn5o3wYeneMwYEePAgC8PGlwff&t=4309" target="_blank" aria-label="External Link">
                  <Icon name="Youtube" />
                </a>
              </td>
             </tr>

             <tr>  
              <td className="overline year">2018</td>
              <td className="title">StepStone Challenge</td>
              <td className="company hide-on-mobile">2<sup>nd</sup></td>
              <td className="tech hide-on-mobile">E-Sports competition / Sports between students and companies</td>
              <td className="links">
                <a href="https://diplomeo.com/actualite-stepstone_digital_challenge_esport"  target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
                <a href="https://youtu.be/kuzoR9LCUzw"  target="_blank" aria-label="External Link">
                  <Icon name="Youtube" />
                </a>
              </td>
             </tr>

             <tr>  
              <td className="overline year">2018</td>
              <td className="title">Grosse Ligue 1<sup>st</sup> segment</td>
              <td className="company hide-on-mobile">3<sup>th</sup></td>
              <td className="tech hide-on-mobile">First step with the 4eSport team</td>
              <td className="links">
                <a href="https://www.efrei.fr/tournoi-esport-universitaire-association-4esport/" target="_blank" aria-label="External Link">
                  <Icon name="External" />
                </a>
              </td>
             </tr>
            </tbody>
          </table>
        </StyledTableContainer>
      </main>
    </Layout>
  );
};
ArchivePage.propTypes = {
  location: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
};

export default ArchivePage;

export const pageQuery = graphql`
  {
    allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/esports/" } }
      sort: { fields: [frontmatter___date], order: DESC }
    ) {
      edges {
        node {
          frontmatter {
            date
            title
            tech
            github
            external
            ios
            android
            company
          }
          html
        }
      }
    }
  }
`;
